/* ============================================================================
   ALLORA SKU SEARCH — assets/allora-sku-search.js

   What this does:
   1. Adds `variants.sku` to the fields searched by the theme's predictive
      search (Focal doesn't send it, so SKU queries fail in the dropdown).
   2. Builds a client-side index of the whole catalog (from
      /collections/all?view=sku-index) and runs a normalized,
      token-AND-substring search on every keystroke — the same algorithm as
      the technical-resources page: lowercase, strip punctuation, every
      token must appear somewhere in the record.
   3. Injects a live "Top matches" panel into the search drawer and a
      "Top matches" strip at the top of the /search results page whenever
      the query looks SKU-like (or native search found nothing).

   Config comes from window.AlloraSkuSearchConfig, set in
   snippets/allora-sku-search.liquid.
   ========================================================================= */
(function () {
  'use strict';

  var CFG = window.AlloraSkuSearchConfig || {};
  var SEARCH_URL = CFG.searchUrl || '/search';
  var INDEX_URL = CFG.indexUrl || '/collections/all?view=sku-index';
  var MAX_DROPDOWN = CFG.maxDropdown || 6;
  var MAX_TOP = CFG.maxTopMatches || 12;
  var CACHE_KEY = 'alloraSkuIdx:' + (CFG.version || '0') + ':' + (CFG.b2b ? 'b2b' : 'retail');
  var CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
  var SUGGEST_FIELDS = 'title,product_type,variants.title,variants.sku,vendor,tag';

  /* ------------------------------------------------------------------ *
   * 1. Make the theme's own predictive search include variants.sku
   * ------------------------------------------------------------------ */
  function amendSuggestUrl(url) {
    if (typeof url !== 'string') return url;
    if (url.indexOf('/search/suggest') === -1) return url;
    if (url.indexOf('fields') !== -1) return url; // theme already sends fields
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'resources%5Boptions%5D%5Bfields%5D=' + encodeURIComponent(SUGGEST_FIELDS);
  }

  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        if (typeof input === 'string') {
          input = amendSuggestUrl(input);
        } else if (input && typeof input.url === 'string') {
          var amended = amendSuggestUrl(input.url);
          if (amended !== input.url) input = new Request(amended, input);
        }
      } catch (e) { /* never break the theme */ }
      return origFetch.call(this, input, init);
    };
  }

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try { url = amendSuggestUrl(url); } catch (e) { /* noop */ }
    var args = Array.prototype.slice.call(arguments);
    args[1] = url;
    return origOpen.apply(this, args);
  };

  /* ------------------------------------------------------------------ *
   * 2. Normalization + index + matching (tech-resources-page semantics)
   * ------------------------------------------------------------------ */

  // "32\" sink" -> "32 inch sink"; strip everything non-alphanumeric.
  function normText(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/(\d)\s*["“”″]/g, '$1 inch ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Query -> tokens. Hyphens/spaces/dots all become token breaks, so
  // "KSN-3218", "ksn 3218" and "ksn3218" all resolve consistently.
  function tokenize(q) {
    return normText(q).split(' ').filter(function (t) { return t.length > 0; });
  }

  function squash(s) { // "KSN-3218-9-S" -> "ksn32189s"
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  // Characters customers mistype when reading a code off a product label.
  var CONFUSABLES = { '0': 'o', '1': 'l', '|': 'l', '5': 's', '8': 'b', '2': 'z' };

  // "VCS-1417-0" and "VCS-1417-O" both collapse to "vcslhlzo" so a misread
  // label still finds the product. Indexed alongside the squashed SKU, never
  // instead of it, so an exact code keeps outranking a lookalike.
  function confusable(s) {
    var sq = squash(s);
    var out = '';
    for (var i = 0; i < sq.length; i++) {
      var c = sq.charAt(i);
      out += CONFUSABLES[c] || c;
    }
    return out;
  }

  // Tolerance from the ticket: 1 edit for short queries, 2 for longer ones.
  function toleranceFor(s) { return s.length <= 5 ? 1 : 2; }

  // Levenshtein with transposition (Damerau), capped so it bails as soon as the
  // best possible distance exceeds max. Returns max + 1 when it gives up.
  function editDistance(a, b, max) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev2 = null;
    var prev = new Array(b.length + 1);
    for (var j = 0; j <= b.length; j++) prev[j] = j;

    for (var i = 1; i <= a.length; i++) {
      var cur = new Array(b.length + 1);
      cur[0] = i;
      var rowMin = cur[0];
      for (var k = 1; k <= b.length; k++) {
        var cost = a.charAt(i - 1) === b.charAt(k - 1) ? 0 : 1;
        var val = Math.min(prev[k] + 1, cur[k - 1] + 1, prev[k - 1] + cost);
        // Transposition, so "1471" against "1417" costs 1 rather than 2.
        if (i > 1 && k > 1 && a.charAt(i - 1) === b.charAt(k - 2) && a.charAt(i - 2) === b.charAt(k - 1)) {
          val = Math.min(val, prev2[k - 2] + 1);
        }
        cur[k] = val;
        if (val < rowMin) rowMin = val;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev;
      prev = cur;
    }
    return prev[b.length];
  }

  var index = null;      // array of prepared records
  var indexPromise = null;

  function prepare(products) {
    return products.map(function (p) {
      var skus = (p.s || []).filter(function (s) { return s && String(s).trim().length; });
      var skuSquashed = skus.map(squash);
      var skuConfusable = skus.map(confusable);
      // SKU blob holds both spaced and squashed forms so that
      // "3218", "ksn 3218", "ksn-3218" and "ksn3218" all hit.
      var skuBlob = skus.map(normText).join(' ') + ' ' + skuSquashed.join(' ');
      var blob = [normText(p.t), normText(p.ty), normText(p.v), normText(p.tg), skuBlob].join(' ');
      return {
        title: p.t, titleType: p.ty || '', url: p.u, img: p.img || null, price: p.p, comparePrice: p.cp || null,
        available: p.av !== 0, skus: skus, skuSquashed: skuSquashed, skuConfusable: skuConfusable,
        skuBlob: ' ' + skuBlob + ' ', blob: ' ' + blob + ' '
      };
    });
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;

    indexPromise = new Promise(function (resolve) {
      // Session cache
      try {
        var cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        if (cached && cached.ts && (Date.now() - cached.ts) < CACHE_TTL_MS && cached.products) {
          index = prepare(cached.products);
          resolve(index);
          return;
        }
      } catch (e) { /* fall through to fetch */ }

      var all = [];
      function fetchPage(page) {
        fetch(INDEX_URL + '&page=' + page, { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            all = all.concat(data.products || []);
            if (page < data.pages) {
              fetchPage(page + 1);
            } else {
              try {
                // Clear stale versions of our cache, then store.
                Object.keys(localStorage).forEach(function (k) {
                  if (k.indexOf('alloraSkuIdx:') === 0 && k !== CACHE_KEY) localStorage.removeItem(k);
                });
                localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), products: all }));
              } catch (e) { /* storage full/unavailable — fine */ }
              index = prepare(all);
              resolve(index);
            }
          })
          .catch(function () { resolve(null); }); // fail silently: theme search still works
      }
      fetchPage(1);
    });
    return indexPromise;
  }

  function tokenInBlob(blob, tok) {
    if (blob.indexOf(tok) !== -1) return true;
    // light plural handling: "sinks" matches "sink"
    if (tok.length > 3 && tok.charAt(tok.length - 1) === 's' && blob.indexOf(tok.slice(0, -1)) !== -1) return true;
    return false;
  }

  function search(query, limit) {
    if (!index) return { results: [], skuIntent: false };
    var tokens = tokenize(query);
    if (!tokens.length) return { results: [], skuIntent: false };
    var qSquash = tokens.join('');
    var qConfusable = confusable(qSquash);
    var out = [];

    for (var i = 0; i < index.length; i++) {
      var rec = index[i];
      // A lookalike query ("vcs-1417-0") shares no literal token with the real
      // SKU, so fall back to comparing confusable forms before discarding it.
      var ok = true;
      for (var j = 0; j < tokens.length; j++) {
        if (!tokenInBlob(rec.blob, tokens[j])) { ok = false; break; }
      }
      if (!ok) {
        ok = rec.skuConfusable.some(function (c) {
          return c === qConfusable || c.indexOf(qConfusable) !== -1;
        });
      }
      if (!ok) continue;

      var score = 0;
      var skuHit = false;
      for (var k = 0; k < rec.skuSquashed.length; k++) {
        if (rec.skuSquashed[k] === qSquash) { score = Math.max(score, 1000); skuHit = true; }
        else if (rec.skuSquashed[k].indexOf(qSquash) === 0) { score = Math.max(score, 500); skuHit = true; }
        else if (rec.skuSquashed[k].indexOf(qSquash) !== -1) { score = Math.max(score, 300); skuHit = true; }
      }
      // Scored below every literal tier above, so an exact code always wins.
      for (var n = 0; n < rec.skuConfusable.length; n++) {
        if (rec.skuConfusable[n] === qConfusable) { score = Math.max(score, 900); skuHit = true; }
        else if (rec.skuConfusable[n].indexOf(qConfusable) === 0) { score = Math.max(score, 450); skuHit = true; }
        else if (rec.skuConfusable[n].indexOf(qConfusable) !== -1) { score = Math.max(score, 280); skuHit = true; }
      }
      if (!skuHit) {
        // did every token appear within the SKU field alone?
        var allInSku = tokens.every(function (t) { return tokenInBlob(rec.skuBlob, t); });
        var someInSku = tokens.some(function (t) { return tokenInBlob(rec.skuBlob, t); });
        if (allInSku) { score = Math.max(score, 200); skuHit = true; }
        else if (someInSku) { score = Math.max(score, 50); skuHit = true; }
      }
      // Ranking nudges: word-start title matches and type matches beat
      // incidental substring hits (keeps accessories below actual products).
      for (var m = 0; m < tokens.length; m++) {
        if ((' ' + normText(rec.title) + ' ').indexOf(' ' + tokens[m]) !== -1) score += 3;
        if ((' ' + normText(rec.titleType || '') + ' ').indexOf(' ' + tokens[m] + ' ') !== -1) score += 15;
      }
      if (!rec.available) score -= 20;

      out.push({ rec: rec, score: score, skuHit: skuHit });
    }

    // Nothing matched literally, so fall back to edit distance for typos and
    // transposed digits. Capped well below the literal tiers, and only for
    // SKU-shaped queries so word searches are never affected.
    if (!out.length && qConfusable.length >= 3 && /[0-9]/.test(qSquash)) {
      var tol = toleranceFor(qConfusable);
      for (var f = 0; f < index.length; f++) {
        var frec = index[f];
        var best = tol + 1;
        for (var g = 0; g < frec.skuConfusable.length; g++) {
          var d = editDistance(qConfusable, frec.skuConfusable[g], tol);
          if (d < best) best = d;
        }
        if (best > tol) continue;
        // 120 then 60: always under the 200 "all tokens in SKU" tier.
        var fscore = best === 1 ? 120 : 60;
        if (!frec.available) fscore -= 20;
        out.push({ rec: frec, score: fscore, skuHit: true, fuzzy: true, distance: best });
      }
    }

    out.sort(function (a, b) { return b.score - a.score || a.rec.title.localeCompare(b.rec.title); });

    // Only claim the query as "SKU intent" on STRONG evidence: the query is a
    // SKU / SKU fragment / SKU+word combo (score >= 200), not merely a digit
    // (so "32 inch kitchen sink" stays with native search + filters).
    var strongSku = out.length > 0 && out.some(function (r) { return r.score >= 200; });
    // A fuzzy-only result set is the case the native dropdown already failed,
    // so treat it as SKU intent too or the panel would never show.
    var fuzzyOnly = out.length > 0 && out.every(function (r) { return r.fuzzy; });
    return {
      results: out.slice(0, limit),
      total: out.length,
      skuIntent: strongSku || fuzzyOnly,
      fuzzyOnly: fuzzyOnly
    };
  }

  /* ------------------------------------------------------------------ *
   * 3. UI
   * ------------------------------------------------------------------ */
  var STYLE = '' +
    '.allora-sku-panel{margin:6px 0 10px;border:1px solid rgba(0,0,0,.09);border-radius:6px;background:#fff;overflow:hidden}' +
    '.allora-sku-panel[hidden]{display:none}' +
    '.allora-sku-panel__heading{display:block;font-size:11px;letter-spacing:.06em;text-transform:uppercase;opacity:.6;padding:10px 14px 4px}' +
    '.allora-sku-item{display:flex;align-items:center;gap:12px;padding:8px 14px;text-decoration:none;color:inherit}' +
    '.allora-sku-item:hover{background:rgba(0,0,0,.04)}' +
    '.allora-sku-item__img{width:44px;height:44px;object-fit:contain;flex:0 0 44px;border-radius:4px;background:#f6f6f6}' +
    '.allora-sku-item__noimg{width:44px;height:44px;flex:0 0 44px;border-radius:4px;background:#f2f2f2}' +
    '.allora-sku-item__info{min-width:0}' +
    '.allora-sku-item__badge{display:inline-block;background:rgba(0,0,0,.72);color:#fff;font-size:10px;line-height:1;padding:3px 5px;border-radius:3px;white-space:nowrap;margin-bottom:3px}' +
    '.allora-sku-item__title{display:block;font-size:13px;line-height:1.35;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
    '.allora-sku-item__price{font-size:12px;opacity:.75}' +
    '.allora-sku-item__compare{text-decoration:line-through;opacity:.55;margin-left:5px}' +
    '.allora-sku-panel__all{display:block;padding:9px 14px;font-size:12px;border-top:1px solid rgba(0,0,0,.07);text-decoration:none;color:inherit;opacity:.8}' +
    '.allora-sku-panel__all:hover{opacity:1;background:rgba(0,0,0,.04)}' +
    '.allora-sku-top{margin:0 0 28px}' +
    '.allora-sku-top__heading{font-size:14px;font-weight:600;margin:0 0 12px}' +
    '.allora-sku-top__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(210px,1fr));gap:10px}' +
    '.allora-sku-top .allora-sku-item{border:1px solid rgba(0,0,0,.09);border-radius:6px;background:#fff}' +
    /* inline header search bar (logo_center_search_open layout): float the panel */
    '.header__search-bar{position:relative}' +
    '.header__search-bar .allora-sku-panel{position:absolute;top:100%;left:0;right:0;z-index:12;margin:4px 0 0;box-shadow:0 8px 24px rgba(0,0,0,.14)}' +
    /* injected inside a dropdown that already has its own frame */
    '.allora-sku-panel--inline{position:static!important;margin:0;border:0;box-shadow:none}';

  function injectStyle() {
    if (document.getElementById('allora-sku-style')) return;
    var el = document.createElement('style');
    el.id = 'allora-sku-style';
    el.textContent = STYLE;
    document.head.appendChild(el);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Pick the SKU to show on the badge: the one the query actually matched.
  function badgeSku(hit, tokens) {
    var skus = hit.rec.skus;
    if (!skus.length) return '';
    for (var i = 0; i < skus.length; i++) {
      var sq = hit.rec.skuSquashed[i];
      var allIn = tokens.every(function (t) { return sq.indexOf(t) !== -1; });
      if (allIn) return skus[i];
    }
    // Lookalike query: match on the confusable form so the badge shows the
    // SKU the customer actually meant rather than the first variant.
    var qConf = confusable(tokens.join(''));
    for (var j = 0; j < skus.length; j++) {
      if (hit.rec.skuConfusable[j].indexOf(qConf) !== -1) return skus[j];
    }
    return skus[0];
  }

  function itemHtml(hit, tokens) {
    var rec = hit.rec;
    var sku = badgeSku(hit, tokens);
    return '<a class="allora-sku-item" href="' + escapeHtml(rec.url) + '">' +
      (rec.img
        ? '<img class="allora-sku-item__img" loading="lazy" src="' + escapeHtml(rec.img) + '" alt="">'
        : '<span class="allora-sku-item__noimg"></span>') +
      '<span class="allora-sku-item__info">' +
      (sku ? '<span class="allora-sku-item__badge">' + escapeHtml(sku) + '</span>' : '') +
      '<span class="allora-sku-item__title">' + escapeHtml(rec.title) + '</span>' +
      '<span class="allora-sku-item__price">' + escapeHtml(rec.price) +
      (rec.comparePrice ? '<span class="allora-sku-item__compare">' + escapeHtml(rec.comparePrice) + '</span>' : '') +
      '</span></span></a>';
  }

  /* ---- 3a. Predictive dropdown panel (search drawer + inline bar) ---- */
  var panels = []; // one panel per search form

  function getPanel(form) {
    for (var i = 0; i < panels.length; i++) {
      if (panels[i].form === form) return panels[i].el;
    }
    var el = document.createElement('div');
    el.className = 'allora-sku-panel';
    el.hidden = true;
    form.parentNode.insertBefore(el, form.nextSibling);
    panels.push({ form: form, el: el });
    return el;
  }

  function renderPanel(form, query) {
    var panel = getPanel(form);
    var q = (query || '').trim();
    if (q.length < 2 || !index) { panel.hidden = true; return; }

    var res = search(q, MAX_DROPDOWN);
    // Only take over when the query looks like a SKU (contains digits or
    // matched SKU codes). Plain word queries are left to the theme.
    if (!res.results.length || !res.skuIntent) { panel.hidden = true; return; }

    var tokens = tokenize(q);
    var html = '<span class="allora-sku-panel__heading">Top matches</span>';
    for (var i = 0; i < res.results.length; i++) html += itemHtml(res.results[i], tokens);
    if (res.total > res.results.length) {
      html += '<a class="allora-sku-panel__all" href="' + escapeHtml(SEARCH_URL) +
        '?q=' + encodeURIComponent(q) + '&type=product">View all ' + res.total + ' matching products</a>';
    }
    panel.innerHTML = html;
    panel.hidden = false;
  }

  function isSearchInput(el) {
    if (!el || el.tagName !== 'INPUT' || el.name !== 'q') return false;
    var form = el.closest('form');
    return !!(form && form.action && form.action.indexOf(SEARCH_URL) !== -1);
  }

  // The in-drawer panel is OFF by default: the fetch patch above already makes
  // the theme's own dropdown SKU-aware, and injecting a second panel into the
  // drawer duplicates results (and can land in the wrong container depending
  // on drawer markup). Set dropdownPanel: true in the snippet to re-enable.
  var debounceTimer = null;
  document.addEventListener('input', function (e) {
    if (!isSearchInput(e.target)) return;
    var input = e.target;
    loadIndex();
    if (!CFG.dropdownPanel) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      renderPanel(input.closest('form'), input.value);
    }, 100);
  }, true);

  // Warm the index as soon as any search UI is touched.
  document.addEventListener('focusin', function (e) {
    if (isSearchInput(e.target)) loadIndex();
  }, true);

  /* ---- 3a-ii. Fill the native dropdown when it finds nothing ---- */

  // Native search can't know "0" was typed for "o". Swap our matches in only
  // when it renders the empty state, so results are never duplicated.
  var EMPTY_SELECTOR = '.hsd__empty, .predictive-search__empty';
  var RESULT_CONTAINERS = '[data-hsd-results], [data-hsm-results], .predictive-search__results-categories';

  function currentQuery(container) {
    var root = container.closest('.header-tt__search, .hsm, predictive-search, .predictive-search');
    var input = root && root.querySelector('input[name="q"]');
    return input ? input.value.trim() : '';
  }

  function rescueEmptyDropdown(container) {
    if (!container.querySelector(EMPTY_SELECTOR)) return;
    var q = currentQuery(container);
    if (q.length < 2) return;

    loadIndex().then(function () {
      if (!index) return;
      // The user may have typed on while the index loaded.
      if (currentQuery(container) !== q || !container.querySelector(EMPTY_SELECTOR)) return;

      var res = search(q, MAX_DROPDOWN);
      if (!res.results.length || !res.skuIntent) return;

      var tokens = tokenize(q);
      var heading = res.fuzzyOnly ? 'No exact match. Did you mean' : 'Top matches';
      var html = '<div class="allora-sku-panel allora-sku-panel--inline">' +
        '<span class="allora-sku-panel__heading">' + heading + '</span>';
      for (var i = 0; i < res.results.length; i++) html += itemHtml(res.results[i], tokens);
      if (res.total > res.results.length) {
        html += '<a class="allora-sku-panel__all" href="' + escapeHtml(SEARCH_URL) +
          '?q=' + encodeURIComponent(q) + '&type=product">View all ' + res.total + ' matching products</a>';
      }
      html += '</div>';
      container.innerHTML = html;
    });
  }

  function watchDropdowns() {
    var seen = [];
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) rescueEmptyDropdown(records[i].target);
    });
    // Containers are created when the search UI opens, so re-scan on focus.
    function attach() {
      var nodes = document.querySelectorAll(RESULT_CONTAINERS);
      for (var i = 0; i < nodes.length; i++) {
        if (seen.indexOf(nodes[i]) !== -1) continue;
        seen.push(nodes[i]);
        observer.observe(nodes[i], { childList: true, subtree: true });
      }
    }
    attach();
    document.addEventListener('focusin', function (e) {
      if (isSearchInput(e.target)) setTimeout(attach, 0);
    }, true);
  }

  /* ---- 3b. "Top matches" strip on the /search results page ---- */
  function enhanceSearchPage() {
    var section = document.querySelector('.shopify-section--main-search');
    if (!section) return;
    var params = new URLSearchParams(location.search);
    var q = (params.get('q') || '').trim();
    if (!q) return;

    loadIndex().then(function () {
      if (!index) return;
      var res = search(q, MAX_TOP);
      if (!res.results.length) return;

      var nativeEmpty = !!section.querySelector('.empty-state');
      if (!res.skuIntent && !nativeEmpty) return; // native handles plain word queries fine

      var container = section.querySelector('.main-search__results');
      var host = container ? container.parentNode : section.querySelector('.container');
      if (!host) return;

      var tokens = tokenize(q);
      var strip = document.createElement('div');
      strip.className = 'allora-sku-top';
      var topHeading = res.fuzzyOnly
        ? 'No exact match for &ldquo;' + escapeHtml(q) + '&rdquo;. Did you mean'
        : 'Top matches for &ldquo;' + escapeHtml(q) + '&rdquo;';
      var html = '<p class="allora-sku-top__heading">' + topHeading +
        (res.total > res.results.length ? ' <span style="font-weight:400;opacity:.6">(showing ' + res.results.length + ' of ' + res.total + ')</span>' : '') +
        '</p><div class="allora-sku-top__grid">';
      for (var i = 0; i < res.results.length; i++) html += itemHtml(res.results[i], tokens);
      html += '</div>';
      strip.innerHTML = html;
      host.insertBefore(strip, container || host.firstChild);
    });
  }

  /* ------------------------------------------------------------------ *
   * Console/debug handle — e.g. AlloraSkuSearch.search('ksn 3218')
   * ------------------------------------------------------------------ */
  window.AlloraSkuSearch = {
    search: function (q, limit) { return search(q, limit || 10); },
    ready: loadIndex,
    rebuild: function () {
      try { localStorage.removeItem(CACHE_KEY); } catch (e) { /* noop */ }
      indexPromise = null; index = null;
      return loadIndex();
    },
    _setIndex: function (products) { index = prepare(products); }
  };

  /* ------------------------------------------------------------------ */
  function init() {
    injectStyle();
    enhanceSearchPage();
    watchDropdowns();
    // Preload the index shortly after page load on the search page or
    // when the header search exists, so the first keystroke is instant.
    setTimeout(loadIndex, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();